import { useState, ReactNode, useMemo } from 'react';
import { createEntityAdapter } from '@reduxjs/toolkit';
import createSafeContext from '@paradigm/utils/createSafeContext';
import { moveItem } from '@paradigm/utils/src/array';
import { FilterViewState } from './filter-provider';

interface TabsView {
  readonly tabs: readonly Tab[];
  readonly activeTab: Tab;
}

export interface TabsState {
  readonly ids: string[];
  readonly entities: { readonly [id: string]: Tab };
}

interface TabsActions {
  readonly viewTab: (tabId: string) => void;
  readonly setTab: (tab: Tab) => void;
  readonly addTab: (id: string, name: string, setActive: boolean) => void;
  readonly getTabById: (tabId: string) => Tab | undefined;
  readonly deleteTabById: (tabId: string) => void;
  readonly deleteAllTabs: () => void;
  readonly reorderTab: (srcIndex: number, destIndex: number) => void;
}

export interface Tab {
  readonly id: string;
  readonly name: string;
  readonly filter: FilterViewState | null;
  readonly index: number;
}

export const [useTabsActions, TabsActionsProvider] =
  createSafeContext<TabsActions>('TabActions');
export const [useTabsView, TabsViewProvider] =
  createSafeContext<TabsView>('TabView');

export const MAIN_TAB_ID = 'main';
export const DEFAULT_TAB = {
  id: MAIN_TAB_ID,
  name: 'Main',
  filter: null,
  index: 0,
};

export const INITIAL_TAB_STATE: TabsState = {
  ids: [MAIN_TAB_ID],
  entities: { [MAIN_TAB_ID]: DEFAULT_TAB },
};

export const MAX_TABS_LENTH = 10;

const sortTabs = (tabA: Tab, tabB: Tab) => tabA.index - tabB.index;

export const tabsAdapter = createEntityAdapter<Tab>({ sortComparer: sortTabs });
const selectors = tabsAdapter.getSelectors();

interface Props {
  readonly children: ReactNode;
  readonly tabsState: TabsState;
  readonly setTabsState: (tab: TabsState) => void;
}

export default function TabsProvider({
  children,
  tabsState,
  setTabsState,
}: Props) {
  const [activeTabId, setActiveTabId] = useState<string>(DEFAULT_TAB.id);

  const actions: TabsActions = useMemo(
    () => ({
      getTabById: (tabId) => selectors.selectById(tabsState, tabId),
      viewTab: (tabId) => {
        const tab = actions.getTabById(tabId);
        if (tab == null) return;
        setActiveTabId(tab.id);
      },
      setTab: (tab) => setTabsState(tabsAdapter.setOne(tabsState, tab)),
      addTab: (id, name, setActive = false) => {
        const allTabs = selectors.selectAll(tabsState);
        if (allTabs.length >= MAX_TABS_LENTH) return;
        actions.setTab({ id, name, index: tabsState.ids.length, filter: null });
        if (setActive) {
          setActiveTabId(id);
        }
      },
      deleteTabById: (tabId) => {
        if (tabId === MAIN_TAB_ID) return;
        if (tabId === activeTabId) {
          setActiveTabId(MAIN_TAB_ID);
        }
        setTabsState(tabsAdapter.removeOne(tabsState, tabId));
      },
      deleteAllTabs: () => {
        const allTabs = selectors.selectAll(tabsState);
        const idsToRemove = allTabs
          .filter((tab) => tab.id !== MAIN_TAB_ID)
          .map((tab) => tab.id);
        setTabsState(tabsAdapter.removeMany(tabsState, idsToRemove));
      },
      reorderTab: (srcIndex, destIndex) => {
        if (srcIndex === DEFAULT_TAB.index || destIndex === DEFAULT_TAB.index)
          return;

        const allTabs = selectors.selectAll(tabsState);
        const movedItems = moveItem(allTabs, srcIndex, destIndex);
        const entities = Object.fromEntries(
          movedItems.map((tab, index) => [tab.id, { ...tab, index }]),
        );
        const ids = Object.values(entities)
          .sort(sortTabs)
          .map((t) => t.id);
        setTabsState({ ids, entities });
      },
    }),
    [activeTabId, setTabsState, tabsState],
  );

  const view = useMemo(
    () => ({
      activeTab: selectors.selectById(tabsState, activeTabId) ?? DEFAULT_TAB,
      tabs: selectors.selectAll(tabsState),
    }),
    [activeTabId, tabsState],
  );

  return (
    <TabsActionsProvider value={actions}>
      <TabsViewProvider value={view}>{children}</TabsViewProvider>
    </TabsActionsProvider>
  );
}
